import {
    useEffect,
    useMemo,
} from 'react';

import {
    addToSelection,
    clearSelection as clearSelectionAction,
    removeFromSelection,
} from '../reducers';
import {selectionSelector} from '../selectors';
import {
    useAppDispatch,
    useAppSelector,
} from '../../../hooks/useTypedReactRedux';

import {Certificate} from '../types';

// FNP-1193 CRUD half — SELECTION.
//
// FNP-1300: this used to be a module-scoped `let globalSelection` plus a `Set`
// of listener callbacks — state living outside React. That shape survived
// unmount and route changes, so an operator who ticked a certificate,
// navigated away and came back found the checkbox still ticked against a table
// that had since refetched. It also outlived a test case, which is why it
// shipped with a `resetCertificatesSelection` export for the suite to call by
// hand: a workaround for the leak rather than a fix for it.
//
// Selection now lives in the Redux slice the module checklist §3 requires,
// built from `SelectionSliceFor` like zones, dns-records, domain-names,
// registrars and top-level-domains.
//
// ⚠️ THE HOOK'S CONTRACT IS UNCHANGED on purpose. It still returns
// `selectedRecords` as an ARRAY and still takes a whole array in
// `setSelectedRecords`, because that is what `MantineDataTable`'s
// `selectedRecords` / `onSelectedRecordsChange` pair speaks. The slice stores a
// map keyed by id; this hook is the one place that converts between the two, so
// no caller had to change.
//
// ⚠️ THE FULL CERTIFICATE IS STORED, not a bare id. The delete confirmation
// names the certificate and lists what it covers, and the in-use refusal needs
// those covered names to explain what would have gone offline.
export const useCertificatesSelection = () => {

    const dispatch = useAppDispatch();

    const selection = useAppSelector((state) => selectionSelector(state));

    // The array `MantineDataTable` wants, derived from the slice's map.
    // Memoised on the slice: a new array identity every render would make the
    // table treat an unchanged selection as a change.
    const selectedRecords = useMemo(
        () => Object.values(selection) as Certificate[],
        [selection],
    );

    // ⚠️ THE TABLE HANDS BACK THE WHOLE NEXT SELECTION, not a delta — it has no
    // add/remove events to offer. So this diffs against what is stored and
    // dispatches only the rows that actually entered or left, rather than
    // clearing and re-adding everything on every tick.
    const setSelectedRecords = (next: Certificate[]) => {

        const nextIds = new Set(next.map((certificate) => certificate.id));

        Object.keys(selection)
              .filter((id) => !nextIds.has(id))
              .forEach((id) => dispatch(removeFromSelection(id)));

        next.filter((certificate) => typeof selection[certificate.id] === 'undefined')
            .forEach((certificate) => dispatch(addToSelection(certificate)));
    };

    // ⚠️ FNP-1300 - CLEAR ON UNMOUNT. Moving to Redux alone does NOT fix the
    // leak: FNP-1293 migrated zones and dns-records, every test went green, and
    // the checkbox was still ticked after a route change because Redux state
    // outlives an unmount exactly as the singleton did. Written in from the
    // start here rather than discovered in a browser a second time.
    //
    // Safe for the delete path: `clearSelection` there fires after a successful
    // mutation while the component is still mounted, so it has already run.
    useEffect(() => () => {
        dispatch(clearSelectionAction());
    }, [dispatch]);

    return {
        selectedRecords,
        setSelectedRecords,
        // The DELETE pattern doc's "clear selection after successful deletion".
        clearSelection: () => dispatch(clearSelectionAction()),
    };
};
